import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatAuthError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = unknown / loading; false = unauthenticated; object = user
    const [user, setUser] = useState(null);

    const fetchMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch (_err) {
            setUser(false);
            return null;
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    useEffect(() => {
        const onExpired = () => setUser(false);
        window.addEventListener("webjesters:auth-expired", onExpired);
        return () => window.removeEventListener("webjesters:auth-expired", onExpired);
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.mfa_required) return { ok: false, mfaRequired: true, challenge: data.challenge };
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return {
                ok: false,
                error: formatAuthError(e),
            };
        }
    }, []);

    const verifyMfa = useCallback(async (challenge, code) => {
        try {
            const { data } = await api.post("/auth/login/mfa", { challenge, code });
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatAuthError(e) };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (_err) {
            /* ignore */
        }
        setUser(false);
    }, []);

    const value = { user, login, verifyMfa, logout, refresh: fetchMe };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
